import { isAnyOf } from "@reduxjs/toolkit";
import { addMinutes, isAfter } from "date-fns";
import { toast } from "react-hot-toast";

import type { AppStartListening } from "@/data/listenerMiddleware";
import { RootState } from "@/data/store";
import { selectHasAutoSubmission, selectIsExam } from "@/features/assignment";
import {
  examStartDatePassed,
  readingDatePassed,
  selectActiveReadingDate,
  selectActiveWritingDate,
  toasts,
  workStartDatePassed,
} from "@/features/timeline";
import { getCurrentDate } from "@/utils/datetime";

import { createDateListener } from "./create-date-listener";

/**
 * Start redux listeners for delivering toasts when certain timeline date points
 * are passed.
 */
export function startTimelineToastListeners(startListening: AppStartListening) {
  // Notify when switching into Reading or Writing time
  startListening({
    matcher: isAnyOf(
      examStartDatePassed,
      readingDatePassed,
      workStartDatePassed
    ),
    effect: async (_, listenerAPI) => {
      const state = listenerAPI.getState();

      // Toast for when reading time begins
      const readingDate = selectActiveReadingDate(state);
      if (readingDate) {
        toasts.reading(readingDate);
      }

      // Toast for when writing time begins
      const writingDate = selectActiveWritingDate(state);
      if (writingDate) {
        const isExam = selectIsExam(state);
        toast.dismiss();
        toasts.writing({ writingDate, isExam });
      }
    },
  });

  // Notify when close to writingDate for submission
  createDateListener(selectSubmissionReminderPoint, (listenerAPI) => {
    const writingDate = selectActiveWritingDate(listenerAPI.getState());
    const autoSubmission = selectHasAutoSubmission(listenerAPI.getState());

    if (writingDate) {
      toasts.submitReminder({
        submissionDate: writingDate,
        autoSubmission,
      });
    }
  })(startListening);

  // Notify when close to lateSubmissionDate
  createDateListener(selectLateSubmissionReminderPoint, () => {
    toasts.lateSubmitReminder();
  })(startListening);
}

/**
 * Select a Timeline date at which a submission reminder should be sent.
 */
const selectSubmissionReminderPoint = (state: RootState) => {
  const timeline = state.timeline;
  const writingDate = timeline.writingDate.date;
  if (writingDate) {
    const reminderDate = addMinutes(new Date(writingDate), -10);
    return {
      date: reminderDate.toISOString(),
      passed: isAfter(getCurrentDate(), reminderDate),
      extended: false,
    };
  } else {
    return {
      date: null,
      passed: true,
      extended: false,
    };
  }
};

/**
 * Select a Timeline date at which a late submission reminder should be sent.
 */
const selectLateSubmissionReminderPoint = (state: RootState) => {
  const timeline = state.timeline;
  const lateSubmissionDate = timeline.examLateSubmissionDate.date;

  if (lateSubmissionDate) {
    const reminderDate = addMinutes(new Date(lateSubmissionDate), -1);
    return {
      date: reminderDate.toISOString(),
      passed: isAfter(getCurrentDate(), reminderDate),
      extended: false,
    };
  } else {
    return {
      date: null,
      passed: true,
      extended: false,
    };
  }
};
